import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { BankhubReconcileRow } from '@repo/shared-types';
import { adminFetchApi } from '../lib/api';

export function useReconcile() {
  return useQuery({
    queryKey: ['admin', 'bankhub', 'reconcile'],
    queryFn: () => adminFetchApi<{ rows: BankhubReconcileRow[] }>('/admin/bankhub/reconcile'),
  });
}

export function useRemap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { orgId: string; companyXid: string }) =>
      adminFetchApi('/admin/bankhub/remap', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      toast.success('Đã gán lại company');
      qc.invalidateQueries({ queryKey: ['admin', 'bankhub', 'reconcile'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Gán lại thất bại'),
  });
}

export function useCompanyStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { xid: string; status: 'Active' | 'Suspended' | 'Terminated' }) =>
      adminFetchApi(`/admin/bankhub/company/${p.xid}/status`, { method: 'POST', body: JSON.stringify({ status: p.status }) }),
    onSuccess: () => {
      toast.success('Đã đổi status');
      qc.invalidateQueries({ queryKey: ['admin', 'bankhub', 'reconcile'] });
    },
    onError: () => toast.error('Đổi status thất bại'),
  });
}

/** Starts BankHub's hosted UNLINK flow for one company's bank account, and
 *  opens the returned link in a new tab — the operator finishes the flow
 *  there, BankHub itself, not inside this admin. */
export function useUnlinkBank() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { companyXid: string; bankXid: string }) =>
      adminFetchApi<{ hostedLinkUrl: string }>(
        `/admin/bankhub/company/${p.companyXid}/unlink/${p.bankXid}`,
        { method: 'POST' },
      ),
    onSuccess: (data) => {
      window.open(data.hostedLinkUrl, '_blank', 'noopener,noreferrer');
      toast.success('Đã mở luồng gỡ liên kết trong tab mới');
      qc.invalidateQueries({ queryKey: ['admin', 'bankhub', 'reconcile'] });
    },
    onError: (e: Error) => toast.error(e.message || 'Không khởi tạo được luồng gỡ liên kết'),
  });
}
